'use client';

import { useState, useMemo, memo } from 'react';
import { motion } from 'framer-motion';
// Import icons directly - Next.js 15 optimizePackageImports handles tree-shaking
import {
  ArrowRight,
  Phone,
  Bot,
  Target,
  TrendingUp,
  Activity,
  BarChart,
  Shield,
  Users,
  Zap,
  RefreshCw,
  Lock,
  ChevronDown,
} from 'lucide-react';
import Link from 'next/link';
import Image from 'next/image';
import dynamic from 'next/dynamic';
import LazyLoad from '../components/LazyLoad';

// Lazy load heavy components for better performance with proper loading priorities
const BlackHoleBackground = dynamic(() => import('../components/BlackHole/BlackHoleBackground'), {
  ssr: false,
  loading: () => <div className="fixed inset-0 bg-black" />,
});

const HeroSection = dynamic(() => import('../components/HeroSection'), {
  loading: () => (
    <div className="min-h-screen flex items-center justify-center">
      <div className="animate-pulse text-white">Loading...</div>
    </div>
  ),
});

// Defer Three.js component until visible in viewport
const OrbitalIntegration = dynamic(
  () => import('../components/OrbitalIntegration'),
  {
    ssr: false,
    loading: () => (
      <div className="w-full h-[400px] sm:h-[500px] md:h-[600px] lg:h-[700px] xl:h-[800px] flex items-center justify-center bg-black/20 backdrop-blur-sm">
        <div className="flex flex-col items-center gap-4">
          <div className="w-16 h-16 border-4 border-orange-400 border-t-transparent rounded-full animate-spin"></div>
          <div className="text-white text-sm">Loading 3D Visualization...</div>
        </div>
      </div>
    ),
  }
);

const TalkToAIButton = dynamic(() => import('../components/TalkToAIButton'), {
  ssr: false,
});

const SocialMediaLinks = dynamic(() => import('../components/SocialMediaLinks'));

// Move static data outside component to prevent re-creation on every render
const personalUseCases = [
  "Daily Planning",
  "Smart Reminders",
  "Quick Research",
  "Voice Journaling",
  "Task Management",
  "Travel Planning",
  "Recipe Suggestions",
  "Learning Support",
  "Creative Writing",
  "Health Tracking",
  "Home Automation",
  "Entertainment",
  "Personal Finance",
  "Meditation Guide",
  "Workout Planning",
  "Social Planning"
];

const faqData = [
  {
    question: "What is 100X Prompt AI Voice Assistant?",
    answer: "100X Prompt is an advanced AI-powered voice assistant designed specifically for sales teams and customer support. It provides real-time voice conversations, helping automate sales processes and enhance customer interactions."
  },
  {
    question: "How does the voice chat system work?",
    answer: "Our voice chat system uses cutting-edge AI technology including OpenAI, Anthropic Claude, and ElevenLabs for natural voice synthesis. Simply click the voice chat button and start speaking naturally with your AI assistant."
  },
  {
    question: "What are voice chat credits and how do they work?",
    answer: "Voice chat credits are the currency for using our AI voice assistant. Each minute of conversation costs a certain number of credits. You can purchase credit packages or subscribe to unlimited plans based on your usage needs."
  },
  {
    question: "Is my conversation data secure and private?",
    answer: "Yes, we prioritize your privacy and security. All conversations are encrypted, and we follow strict data protection protocols. Your personal conversations and business data remain confidential and secure."
  },
  {
    question: "Can I integrate this with my existing sales tools?",
    answer: "Absolutely! Our AI voice assistant is designed to work seamlessly with popular CRM systems, sales platforms, and customer support tools. We provide APIs and integrations for smooth workflow integration."
  },
  {
    question: "What makes 100X Prompt different from other AI assistants?",
    answer: "Unlike general AI assistants, 100X Prompt is specifically optimized for sales and customer support scenarios. It understands business context, sales processes, and can help with lead qualification, customer onboarding, and support ticket resolution."
  }
];

// Define feature type
interface Feature {
  icon: React.ReactNode;
  title: string;
  description: string;
  metric: string;
}

// Memoized feature component to prevent unnecessary re-renders
const FeatureCard = memo(({
  feature,
  index,
  isActive,
  onActivate
}: {
  feature: Feature;
  index: number;
  isActive: boolean;
  onActivate: () => void;
}) => (
  <motion.div
    className={`p-4 sm:p-6 md:p-8 lg:p-10 rounded-xl sm:rounded-2xl md:rounded-3xl border-2 cursor-pointer transition-all duration-500 backdrop-blur-lg touch-manipulation ${
      isActive
        ? 'bg-gradient-to-br from-blue-600/10 to-purple-600/10 border-blue-400/40 shadow-2xl shadow-blue-500/20'
        : 'bg-black/20 border-white/10 hover:border-white/20 hover:bg-black/30'
    }`}
    onMouseEnter={onActivate}
    onClick={onActivate}
    initial={{ opacity: 0, x: -20 }}
    whileInView={{ opacity: 1, x: 0 }}
    viewport={{ once: true }}
    transition={{ delay: index * 0.1 }}
  >
    <div className="flex flex-col sm:flex-row items-start space-y-3 sm:space-y-0 sm:space-x-4 md:space-x-6">
      <div className={`p-2 sm:p-3 md:p-4 rounded-lg sm:rounded-xl md:rounded-2xl ${isActive ? 'bg-blue-500/80' : 'bg-white/10'} backdrop-blur-sm flex-shrink-0`}>
        <div className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8">
          {feature.icon}
        </div>
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold text-white mb-2 sm:mb-3 md:mb-4 leading-tight">{feature.title}</h3>
        <p className="text-gray-200 mb-3 sm:mb-4 md:mb-6 leading-relaxed text-sm sm:text-base md:text-lg">{feature.description}</p>
        <div className="inline-flex items-center bg-green-400/10 border border-green-400/30 rounded-lg sm:rounded-xl md:rounded-2xl px-3 sm:px-4 md:px-6 py-2 sm:py-2 md:py-3 backdrop-blur-md">
          <TrendingUp className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5 text-green-400 mr-2 sm:mr-2 md:mr-3" />
          <span className="text-green-400 font-bold text-xs sm:text-sm md:text-base lg:text-lg">{feature.metric}</span>
        </div>
      </div>
    </div>
  </motion.div>
));

FeatureCard.displayName = 'FeatureCard';

export default function Home() {
  const [activeFeature, setActiveFeature] = useState(0);
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);

  // Memoize features with icons to prevent re-creation
  const features = useMemo(() => [
    {
      icon: <Bot className="w-8 h-8" />,
      title: "Your Personal AI Companion",
      description: "An intelligent assistant that understands you personally, learns your preferences, and adapts to help with daily tasks and decisions",
      metric: "Available 24/7"
    },
    {
      icon: <Phone className="w-8 h-8" />,
      title: "Natural Voice Conversations",
      description: "Talk naturally about anything - get instant answers, set reminders, make plans, or just have a friendly chat whenever you need",
      metric: "Human-like interaction"
    },
    {
      icon: <Target className="w-8 h-8" />,
      title: "Smart Life Organization",
      description: "Automatically manages your schedule, reminds you of important events, and helps organize your daily life effortlessly",
      metric: "Saves 2+ hours daily"
    },
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: "Personalized Intelligence",
      description: "Learns from your habits and preferences to provide increasingly personalized recommendations and assistance over time",
      metric: "Gets smarter daily"
    }
  ], []);

  return (
    <>
      <BlackHoleBackground />
      <div className="min-h-screen text-white relative z-10">

        {/* Navigation - Mobile First Optimized */}
        <motion.nav 
          className="fixed w-full z-50 bg-black/30 backdrop-blur-xl border-b border-white/20 shadow-2xl"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
            <div className="flex justify-between h-14 sm:h-16 md:h-20 items-center">
              <div className="flex items-center space-x-2 sm:space-x-3">
                <motion.div 
                  className="w-10 h-10 sm:w-12 sm:h-12 md:w-16 md:h-16 rounded-lg sm:rounded-xl flex items-center justify-center shadow-lg"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  transition={{ duration: 0.3 }}
                >
                  <Image
                    src="/brand/100Xprompt.svg"
                    alt="100X Prompt Logo"
                    width={64}
                    height={64}
                    className="w-full h-full object-contain"
                  />
                </motion.div>
                <div className="min-w-0 flex-1">
                  <span className="text-sm sm:text-lg md:text-xl lg:text-2xl font-bold bg-gradient-to-r from-orange-400 to-pink-400 bg-clip-text text-transparent tracking-tight block truncate">
                    Personal Assistant
                  </span>
                  <div className="text-xs text-gray-400 font-medium hidden sm:block -mt-1">Your Life Companion</div>
                </div>
                <div className="hidden xl:flex items-center ml-4 bg-green-500/20 px-3 py-1 rounded-full border border-green-400/30 pulse-glow">
                  <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse mr-2"></div>
                  <span className="text-xs text-green-400 font-semibold tracking-wide">READY</span>
                </div>
              </div>
              
              <div className="flex items-center space-x-2 sm:space-x-3 md:space-x-6">
                <div className="hidden lg:flex items-center space-x-6 text-gray-300">
                  <motion.a 
                    href="#features" 
                    className="hover:text-orange-300 transition-all duration-300 font-medium relative group text-sm"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Features
                    <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-orange-400 to-pink-400 transition-all duration-300 group-hover:w-full"></span>
                  </motion.a>
                  <motion.a 
                    href="#examples" 
                    className="hover:text-pink-300 transition-all duration-300 font-medium relative group text-sm"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Examples
                    <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-pink-400 to-purple-400 transition-all duration-300 group-hover:w-full"></span>
                  </motion.a>
                </div>
                
                <div className="flex items-center space-x-2 sm:space-x-3">
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="hidden sm:block"
                  >
                    <Link href="/sign-up" className="px-3 py-2 md:px-5 md:py-2 text-xs sm:text-sm md:text-base font-medium text-gray-300 hover:text-white border border-gray-600 hover:border-orange-400 rounded-lg transition-all duration-300 hover:bg-white/5 backdrop-blur-sm">
                      Sign Up
                    </Link>
                  </motion.div>
                  
                   {/* <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Link href="/waitlist" className="bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 px-3 py-2 sm:px-4 sm:py-2 md:px-6 md:py-3 rounded-lg sm:rounded-xl md:rounded-2xl text-white font-bold text-xs sm:text-sm md:text-base lg:text-lg transition-all duration-300 shadow-xl hover:shadow-orange-500/50 relative overflow-hidden group">
                      <span className="relative z-10">
                        <span className="hidden sm:inline">Join Waitlist</span>
                        <span className="sm:hidden">Join</span>
                      </span>
                      <div className="absolute inset-0 bg-gradient-to-r from-pink-500 to-purple-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    </Link>
                  </motion.div> */}
                </div>
              </div>
            </div>
          </div>
        </motion.nav>

        {/* Hero Section */}
        <HeroSection />

        {/* Personal Use Cases Section */}
        <section id="examples" className="py-12 sm:py-16 md:py-24 lg:py-32 bg-black/10 backdrop-blur-sm overflow-hidden">
          <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
            <motion.div
              className="text-center mb-8 sm:mb-12 md:mb-16 lg:mb-20"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-xl xs:text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold mb-3 sm:mb-4 md:mb-6 leading-tight px-2">
                <span className="text-white">Perfect for </span>
                <span className="bg-gradient-to-r from-orange-400 via-pink-400 to-purple-400 bg-clip-text text-transparent">
                  Everything
                </span>
                <span className="text-white"> you need</span>
              </h2>
              <p className="text-sm xs:text-base sm:text-lg md:text-xl lg:text-2xl text-gray-300 max-w-4xl mx-auto leading-relaxed font-light px-2">
                Your personal AI companion adapts to help with all aspects of daily life
              </p>
            </motion.div>

            {/* Animated Personal Use Cases - CSS Animation for Better Performance */}
            <div className="relative w-full overflow-hidden">
              <div className="scroll-container flex space-x-4 sm:space-x-6 md:space-x-12 lg:space-x-16">
                {[...personalUseCases, ...personalUseCases].map((useCase, index) => (
                  <div
                    key={`${useCase}-${index}`}
                    className="flex-shrink-0 text-lg xs:text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold whitespace-nowrap select-none gradient-text"
                  >
                    {useCase}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <style jsx global>{`
            /* Mobile-first optimizations */
            * {
              -webkit-tap-highlight-color: transparent;
              -webkit-touch-callout: none;
            }

            /* Better scrolling on iOS */
            body {
              -webkit-overflow-scrolling: touch;
            }

            /* Smooth font rendering */
            body {
              -webkit-font-smoothing: antialiased;
              -moz-osx-font-smoothing: grayscale;
            }

            /* GPU-accelerated CSS animations */
            .scroll-container {
              animation: scroll 30s linear infinite;
              will-change: transform;
            }

            @keyframes scroll {
              from { transform: translate3d(0, 0, 0); }
              to { transform: translate3d(-50%, 0, 0); }
            }

            .gradient-text {
              background: linear-gradient(135deg, #f59e0b, #ec4899, #8b5cf6, #06b6d4);
              background-size: 200% 200%;
              -webkit-background-clip: text;
              -webkit-text-fill-color: transparent;
              background-clip: text;
              animation: gradientShift 8s ease-in-out infinite;
            }

            @keyframes gradientShift {
              0%, 100% {
                background-position: 0% 50%;
              }
              50% {
                background-position: 100% 50%;
              }
            }

            .pulse-glow {
              animation: pulseGlow 2s ease-in-out infinite;
            }

            @keyframes pulseGlow {
              0%, 100% {
                box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.4);
              }
              50% {
                box-shadow: 0 0 0 8px rgba(34, 197, 94, 0);
              }
            }
            
            @media (prefers-reduced-motion: reduce) {
              * {
                animation-duration: 0.01ms !important;
                animation-iteration-count: 1 !important;
                transition-duration: 0.01ms !important;
                scroll-behavior: auto !important;
              }
            }
            
            /* Enhanced touch interactions for mobile */
            @media (hover: none) and (pointer: coarse) {
              .touch-manipulation {
                touch-action: manipulation;
                -webkit-tap-highlight-color: transparent;
              }
              
              /* Larger touch targets on mobile */
              button, a, [role="button"] {
                min-height: 44px;
                min-width: 44px;
              }
              
              /* Smooth scrolling on mobile */
              html {
                scroll-behavior: smooth;
                -webkit-overflow-scrolling: touch;
              }
            }
          `}</style>
        </section>

        {/* Features Section */}
        <section id="features" className="py-12 sm:py-16 md:py-24 lg:py-32 bg-black/20 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
            <motion.div 
              className="text-center mb-8 sm:mb-12 md:mb-16 lg:mb-24"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="inline-flex items-center space-x-2 sm:space-x-3 bg-orange-500/10 border border-orange-400/30 rounded-lg sm:rounded-xl md:rounded-2xl px-3 sm:px-4 md:px-6 lg:px-8 py-2 sm:py-3 md:py-4 mb-4 sm:mb-6 md:mb-8 lg:mb-12 backdrop-blur-md">
                <Activity className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5 lg:w-6 lg:h-6 text-orange-400" />
                <span className="text-orange-400 font-bold text-xs sm:text-sm md:text-base lg:text-lg tracking-wide">PERSONAL AI</span>
              </div>
              <h2 className="text-xl xs:text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold bg-gradient-to-r from-white via-orange-100 to-pink-200 bg-clip-text text-transparent mb-3 sm:mb-4 md:mb-6 lg:mb-8 drop-shadow-2xl leading-tight px-2">
                Your Intelligent Life Companion
              </h2>
              <p className="text-sm xs:text-base sm:text-lg md:text-xl lg:text-2xl text-gray-200 max-w-4xl mx-auto leading-relaxed font-light px-2">
                An AI assistant that truly understands you, learns your patterns, and helps make every day smoother and more productive.
              </p>
            </motion.div>

            <div className="grid lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-12 items-center mb-8 sm:mb-12 md:mb-16 lg:mb-20">
              <div className="space-y-4 sm:space-y-6 md:space-y-8">
                {features.map((feature, index) => (
                  <FeatureCard
                    key={index}
                    feature={feature}
                    index={index}
                    isActive={activeFeature === index}
                    onActivate={() => setActiveFeature(index)}
                  />
                ))}
              </div>

              <motion.div
                className="relative mt-6 sm:mt-8 lg:mt-0"
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
              >
                <div className="bg-black/30 backdrop-blur-2xl rounded-xl sm:rounded-2xl md:rounded-3xl p-4 sm:p-6 md:p-8 lg:p-10 border border-white/20 shadow-2xl">
                  <div className="mb-4 md:mb-6">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-2">
                      <h4 className="text-lg md:text-xl font-bold text-white">Your Daily Assistant</h4>
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-orange-400 rounded-full animate-pulse"></div>
                        <span className="text-orange-400 text-xs md:text-sm font-medium">LEARNING</span>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3 md:gap-4">
                      <div className="bg-orange-600/20 rounded-lg p-3 md:p-4 text-center border border-orange-500/30">
                        <div className="text-2xl md:text-3xl font-bold text-orange-400 mb-1">23</div>
                        <div className="text-gray-400 text-xs md:text-sm">Tasks Completed</div>
                      </div>
                      <div className="bg-pink-600/20 rounded-lg p-3 md:p-4 text-center border border-pink-500/30">
                        <div className="text-2xl md:text-3xl font-bold text-pink-400 mb-1">2.5h</div>
                        <div className="text-gray-400 text-xs md:text-sm">Time Saved</div>
                      </div>
                      <div className="bg-purple-600/20 rounded-lg p-3 md:p-4 text-center border border-purple-500/30">
                        <div className="text-2xl md:text-3xl font-bold text-purple-400 mb-1">96%</div>
                        <div className="text-gray-400 text-xs md:text-sm">Accuracy Rate</div>
                      </div>
                      <div className="bg-blue-600/20 rounded-lg p-3 md:p-4 text-center border border-blue-500/30">
                        <div className="text-2xl md:text-3xl font-bold text-blue-400 mb-1">8</div>
                        <div className="text-gray-400 text-xs md:text-sm">Days Learning</div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-2 md:space-y-3">
                    <div className="flex items-center justify-between p-2 md:p-3 bg-gray-700/50 rounded-lg">
                      <span className="text-gray-300 text-sm md:text-base">Morning Routine</span>
                      <span className="text-orange-400 font-semibold text-sm md:text-base">✓ Optimized</span>
                    </div>
                    <div className="flex items-center justify-between p-2 md:p-3 bg-gray-700/50 rounded-lg">
                      <span className="text-gray-300 text-sm md:text-base">Schedule Management</span>
                      <span className="text-pink-400 font-semibold text-sm md:text-base">3 conflicts resolved</span>
                    </div>
                    <div className="flex items-center justify-between p-2 md:p-3 bg-gray-700/50 rounded-lg">
                      <span className="text-gray-300 text-sm md:text-base">Next Reminder</span>
                      <span className="text-purple-400 font-semibold text-sm md:text-base">Lunch in 30min</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Seamless Integration Section */}
        <section className="py-12 sm:py-16 md:py-20 lg:py-24 bg-black/10 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
            <motion.div
              className="text-center mb-4 sm:mb-6 md:mb-8"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-xl xs:text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold mb-3 sm:mb-4 md:mb-6 leading-tight px-2">
                <span className="text-white">Works with </span>
                <span className="bg-gradient-to-r from-orange-400 via-pink-400 to-purple-400 bg-clip-text text-transparent">
                  Everything
                </span>
              </h2>
              <p className="text-sm xs:text-base sm:text-lg md:text-xl lg:text-2xl text-gray-300 max-w-4xl mx-auto leading-relaxed font-light px-2">
                Seamlessly connects with all your favorite apps, devices, and services to create a unified personal experience
              </p>
            </motion.div>

            {/* Main Integration Showcase */}
            <div className="relative">
              {/* Central 3D Orbital System */}
              <motion.div
                className="w-full h-[400px] sm:h-[500px] md:h-[600px] lg:h-[700px] xl:h-[800px] flex justify-center"
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 1.0, ease: "easeOut" }}
              >
                <div className="relative w-full h-full">
                  <LazyLoad
                    rootMargin="300px"
                    fallback={
                      <div className="w-full h-full flex items-center justify-center bg-black/20 backdrop-blur-sm rounded-xl">
                        <div className="flex flex-col items-center gap-4">
                          <div className="w-20 h-20 border-4 border-orange-400 border-t-transparent rounded-full animate-spin"></div>
                          <div className="text-white text-base">Loading 3D Visualization...</div>
                        </div>
                      </div>
                    }
                  >
                    <OrbitalIntegration />
                  </LazyLoad>
                </div>
              </motion.div>

            </div>

            {/* Integration benefits */}
            <motion.div
              className="grid sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 md:gap-8 mt-8 sm:mt-12 md:mt-16 lg:mt-20"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
            >
              <div className="text-center col-span-1">
                <div className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 lg:w-16 lg:h-16 bg-gradient-to-r from-orange-500 to-pink-400 rounded-lg sm:rounded-xl md:rounded-2xl flex items-center justify-center mx-auto mb-2 sm:mb-3 md:mb-4">
                  <Zap className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 lg:w-8 lg:h-8 text-white" strokeWidth={1.5} />
                </div>
                <h3 className="text-base sm:text-lg md:text-xl font-bold text-white mb-1 sm:mb-1 md:mb-2">Instant Setup</h3>
                <p className="text-gray-400 text-xs sm:text-sm md:text-base leading-relaxed">Connect your favorite apps and devices in seconds</p>
              </div>
              <div className="text-center col-span-1">
                <div className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 lg:w-16 lg:h-16 bg-gradient-to-r from-pink-500 to-purple-400 rounded-lg sm:rounded-xl md:rounded-2xl flex items-center justify-center mx-auto mb-2 sm:mb-3 md:mb-4">
                  <RefreshCw className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 lg:w-8 lg:h-8 text-white" strokeWidth={1.5} />
                </div>
                <h3 className="text-base sm:text-lg md:text-xl font-bold text-white mb-1 sm:mb-1 md:mb-2">Personal Sync</h3>
                <p className="text-gray-400 text-xs sm:text-sm md:text-base leading-relaxed">Your data stays unified across all your devices</p>
              </div>
              <div className="text-center col-span-1 sm:col-span-2 md:col-span-1">
                <div className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 lg:w-16 lg:h-16 bg-gradient-to-r from-purple-500 to-blue-400 rounded-lg sm:rounded-xl md:rounded-2xl flex items-center justify-center mx-auto mb-2 sm:mb-3 md:mb-4">
                  <Lock className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 lg:w-8 lg:h-8 text-white" strokeWidth={1.5} />
                </div>
                <h3 className="text-base sm:text-lg md:text-xl font-bold text-white mb-1 sm:mb-1 md:mb-2">Privacy First</h3>
                <p className="text-gray-400 text-xs sm:text-sm md:text-base leading-relaxed">Your personal data stays private and secure</p>
              </div>
            </motion.div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-12 sm:py-16 md:py-24 lg:py-32 bg-black/30 backdrop-blur-lg">
          <div className="max-w-5xl mx-auto text-center px-3 sm:px-4 md:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="space-y-6 sm:space-y-8 md:space-y-10 lg:space-y-12"
            >
              <h2 className="text-xl xs:text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold bg-gradient-to-r from-white via-orange-100 to-pink-200 bg-clip-text text-transparent drop-shadow-2xl leading-tight px-2">
                Ready for Your AI Companion?
              </h2>
              
              <p className="text-sm xs:text-base sm:text-lg md:text-xl lg:text-2xl text-gray-200 max-w-4xl mx-auto leading-relaxed font-light px-2">
                Join thousands of people using Personal AI to enhance their daily lives. 
                Start your intelligent companion journey today.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 md:gap-6 lg:gap-8 justify-center items-center pt-3 sm:pt-4 md:pt-6 lg:pt-8">
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="w-full sm:w-auto"
                >
                  <Link href="/voice-chat" className="block w-full sm:w-auto bg-gradient-to-r from-orange-600 to-pink-500 hover:from-orange-700 hover:to-pink-600 px-6 sm:px-8 md:px-10 lg:px-12 py-3 sm:py-4 md:py-5 lg:py-6 rounded-lg sm:rounded-xl md:rounded-2xl text-white font-bold text-base sm:text-lg md:text-xl lg:text-2xl transition-all shadow-2xl hover:shadow-orange-500/50 text-center touch-manipulation">
                    Talk to AI Now
                    <ArrowRight className="ml-2 sm:ml-3 md:ml-4 w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 lg:w-7 lg:h-7 inline" />
                  </Link>
                </motion.div>
                
                 {/* <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="w-full sm:w-auto"
                >
                  <Link href="/waitlist" className="block w-full sm:w-auto border-2 border-white/20 hover:border-white/40 px-6 sm:px-8 md:px-10 lg:px-12 py-3 sm:py-4 md:py-5 lg:py-6 rounded-lg sm:rounded-xl md:rounded-2xl text-white font-bold text-base sm:text-lg md:text-xl lg:text-2xl transition-all hover:bg-white/5 backdrop-blur-md group text-center touch-manipulation">
                    Join Waitlist
                    <Star className="ml-2 sm:ml-3 md:ml-4 w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 lg:w-7 lg:h-7 inline group-hover:rotate-12 transition-transform" />
                  </Link>
                </motion.div> */}
              </div>
            </motion.div>
          </div>
        </section>

        {/* FAQ Section with Schema Markup */}
        <section className="py-12 sm:py-16 md:py-24 lg:py-32 bg-black/20 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
            <motion.div
              className="text-center mb-8 sm:mb-12 md:mb-16 lg:mb-20"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-xl xs:text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold mb-3 sm:mb-4 md:mb-6 leading-tight px-2">
                <span className="text-white">Frequently Asked </span>
                <span className="bg-gradient-to-r from-orange-400 via-pink-400 to-purple-400 bg-clip-text text-transparent">
                  Questions
                </span>
              </h2>
              <p className="text-sm xs:text-base sm:text-lg md:text-xl lg:text-2xl text-gray-300 max-w-4xl mx-auto leading-relaxed font-light px-2">
                Everything you need to know about 100X Prompt AI Voice Assistant
              </p>
            </motion.div>

            <div className="max-w-4xl mx-auto space-y-4 sm:space-y-6">
              {faqData.map((faq, index) => (
                <motion.div
                  key={index}
                  className="bg-black/30 backdrop-blur-xl border border-white/10 rounded-xl sm:rounded-2xl overflow-hidden hover:border-white/20 transition-all duration-300"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <button
                    className="w-full text-left p-4 sm:p-6 md:p-8 focus:outline-none focus:ring-2 focus:ring-orange-400/50 focus:ring-inset"
                    onClick={() => setOpenFAQ(openFAQ === index ? null : index)}
                  >
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-white pr-4">
                        {faq.question}
                      </h3>
                      <motion.div
                        animate={{ rotate: openFAQ === index ? 180 : 0 }}
                        transition={{ duration: 0.3 }}
                        className="flex-shrink-0"
                      >
                        <ChevronDown className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 text-orange-400" />
                      </motion.div>
                    </div>
                  </button>
                  
                  <motion.div
                    initial={false}
                    animate={{
                      height: openFAQ === index ? "auto" : 0,
                      opacity: openFAQ === index ? 1 : 0
                    }}
                    transition={{ duration: 0.3, ease: "easeInOut" }}
                    className="overflow-hidden"
                  >
                    <div className="px-4 sm:px-6 md:px-8 pb-4 sm:pb-6 md:pb-8 pt-0">
                      <p className="text-gray-300 text-sm sm:text-base md:text-lg leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  </motion.div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* FAQ Schema Markup - Generated from faqData */}
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "FAQPage",
                "mainEntity": faqData.map(faq => ({
                  "@type": "Question",
                  "name": faq.question,
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": faq.answer
                  }
                }))
              })
            }}
          />
        </section>

        {/* Footer */}
        <footer className="bg-black/50 backdrop-blur-lg border-t border-white/10 py-8 sm:py-10 md:py-12 lg:py-16">
          <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
            <motion.div
              className="text-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="flex items-center justify-center space-x-2 sm:space-x-3 mb-4 sm:mb-6 md:mb-8">
                <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 rounded-lg flex items-center justify-center">
                  <Image
                    src="/brand/100Xprompt.svg"
                    alt="100X Prompt Logo"
                    width={48}
                    height={48}
                    className="w-full h-full object-contain"
                  />
                </div>
                <span className="text-lg sm:text-xl md:text-2xl font-bold text-white">Personal AI</span>
              </div>
              
              <div className="flex flex-col sm:flex-row items-center justify-center space-y-2 sm:space-y-0 sm:space-x-4 md:space-x-6 lg:space-x-8 text-gray-400 text-xs sm:text-sm md:text-base">
                <div className="flex items-center space-x-1 sm:space-x-2">
                  <Shield className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5 text-green-400" />
                  <span>Privacy First</span>
                </div>
                <div className="flex items-center space-x-1 sm:space-x-2">
                  <Users className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5 text-orange-400" />
                  <span>Always Available</span>
                </div>
                <div className="flex items-center space-x-1 sm:space-x-2">
                  <BarChart className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5 text-purple-400" />
                  <span>Smart Learning</span>
                </div>
              </div>

              {/* Social Media Links */}
              <motion.div
                className="mt-8 sm:mt-12 pt-8 sm:pt-12 border-t border-gray-600/30"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <div className="text-center">
                  <h3 className="text-lg sm:text-xl font-bold text-white mb-6">
                    Connect with 100X Prompt
                  </h3>
                  <SocialMediaLinks className="justify-center" />
                  <p className="text-gray-400 text-sm mt-4">
                    Follow us for updates, tips, and AI insights
                  </p>
                </div>
              </motion.div>
              
              <div className="mt-4 sm:mt-6 md:mt-8 pt-4 sm:pt-6 md:pt-8 border-t border-gray-800 text-gray-500 text-xs sm:text-sm md:text-base">
                <p>&copy; 2026 Personal AI. All rights reserved. Your intelligent companion.</p>
                <div className="text-gray-400 mt-1 text-xs sm:text-sm">Active Users: 15,247</div>
              </div>
            </motion.div>
          </div>
        </footer>
        {/* Talk to AI Assistant Button */}
        <TalkToAIButton />
      </div>
    </>
  );
}